import React, { useState, useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from "recharts";
import NoDataDisplay from "src/components/reusable/NoDataDisplay";
import CustomPagination from "src/components/reusable/ReusableTable/CustomPagination";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { useTranslation } from "react-i18next";
import { formatNumber, wrapBidiIsolate } from "src/utils/numberFormat";
import {
  CHART_COLOR_PALETTE,
  CHART_TICK_COLOR,
  CHART_AXIS_STROKE,
  CHART_TOOLTIP_CURSOR,
} from "../../../../components/charts/chartPalette";
import "./AudienceChart.scss";

type AudienceData = {
  campaign_name: string;
  members: number;
  [key: string]: string | number;
};

type AudienceBarChartProps = {
  audienceNames: string[];
  data: AudienceData[];
  barKey?: keyof AudienceData;
  campaignColors: string[];
  formatTitle: (key: string) => string;
};

const ITEMS_PER_PAGE = 10;

const MultiLineXAxisTick: React.FC<any> = ({ x, y, payload }) => {
  const text = String(payload?.value ?? "");
  const words = text.split(" ");

  const firstLine = words.slice(0, 3).join(" ");
  const secondLine = words.slice(3).join(" ");

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill={CHART_TICK_COLOR} fontSize={10}>
        <tspan x={0} dy={0}>{firstLine}</tspan>
        {secondLine && <tspan x={0} dy={12}>{secondLine}</tspan>}
      </text>
    </g>
  );
};

const AudienceBarChart: React.FC<AudienceBarChartProps> = ({
  audienceNames,
  data,
  barKey = "members",
  campaignColors,
  formatTitle,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { t, i18n } = useTranslation();
  const chartColors = campaignColors.length > 0 ? campaignColors : CHART_COLOR_PALETTE;

  const paginatedData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    const sortedData = [...data].sort((a, b) => {
      const aValue = barKey in a && typeof a[barKey] === "number" ? Number(a[barKey]) : 0;
      const bValue = barKey in b && typeof b[barKey] === "number" ? Number(b[barKey]) : 0;
      return bValue - aValue;
    });
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [data, currentPage, barKey]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const isArabic = i18n.language?.startsWith("ar");

  const formatYAxisTick = useCallback(
    (value: number) => {
      if (!(typeof barKey === "string" && barKey.includes("rate"))) {
        return formatNumber(value, {}, i18n.language);
      }
      const percentText = `${formatNumber(value, { maximumFractionDigits: 0 }, i18n.language)}%`;
      return isArabic ? wrapBidiIsolate(percentText) : percentText;
    },
    [barKey, i18n.language, isArabic]
  );

  const formatChartTooltip = useCallback(
    (value: number) => {
      if (!(typeof barKey === "string" && barKey.includes("rate"))) {
        return formatNumber(value ?? 0, {}, i18n.language);
      }
      const percentText = `${formatNumber(value ?? 0, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`;
      return isArabic ? wrapBidiIsolate(percentText) : percentText;
    },
    [barKey, i18n.language, isArabic]
  );

  return (
    <div className="chart-section-overview">
      {data?.length > 0 ? (
        <>
          <div className="chart-header-audience">
            <h3 className="chart-main-title">
              {t("audienceOverview.chartTitle")}
            </h3>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={paginatedData}
              margin={{ top: 6, right: 14, bottom: 18, left: 14 }}
              barCategoryGap="26%"
              barGap={4}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={CHART_AXIS_STROKE}
              />

              <XAxis
                dataKey="campaign_name"
                padding={{ left: 10, right: 10 }}
                tick={<MultiLineXAxisTick />}
                interval={0}
                height={50}
                tickLine={false}
                tickMargin={8}
                label={{
                  value: t("audienceOverview.chartXAxisLabel"),
                  position: "insideBottom",
                  offset: -3,
                  style: {
                    textAnchor: "middle",
                    fill: CHART_TICK_COLOR,
                    fontSize: 10,
                    fontWeight: 400,
                  },
                }}
              />

              <YAxis
                axisLine={{ stroke: CHART_AXIS_STROKE }}
                tickLine={false}
                tick={{ fill: CHART_TICK_COLOR, fontSize: 11 }}
                width={50}
                allowDecimals={false}
                domain={[0, "auto"]}
                tickFormatter={formatYAxisTick}
                label={{
                  value: t("audienceOverview.chartYAxisLabel"),
                  angle: -90,
                  position: "left",
                  offset: 6,
                  style: {
                    textAnchor: "middle",
                    fill: CHART_TICK_COLOR,
                    fontSize: 10,
                    fontWeight: 400,
                  },
                }}
              />

              <Tooltip
                cursor={{ fill: CHART_TOOLTIP_CURSOR }}
                content={
                  <CustomChartTooltip
                    title={formatTitle(barKey.toString())}
                    formatTooltip={formatChartTooltip}
                  />
                }
              />

              <Bar
                dataKey={barKey}
                radius={[5, 5, 0, 0]}
                name={formatTitle(barKey.toString())}
                barSize={44}
              >
                {paginatedData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={chartColors[index % chartColors.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Pagination Section */}
          {data?.length > ITEMS_PER_PAGE && (
            <div className="chart-pagination-wrapper">
              <CustomPagination
                current={currentPage}
                pageSize={ITEMS_PER_PAGE}
                total={data.length}
                onChange={handlePageChange}
                showTotal={false}
              />
            </div>
          )}
        </>
      ) : (
        <NoDataDisplay
          message="No data available for the selected period."
          altText="No Data"
        />
      )}
    </div>
  );
};

export default AudienceBarChart;
